import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, ArrowLeft, CheckCircle, AlertCircle } from 'lucide-react';
import Layout from '../components/layout/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { forgotPassword } from '../lib/api';

const ForgotPasswordPage = () => {
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState('');
    const [debugInfo, setDebugInfo] = useState(null);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        setDebugInfo(null);

        try {
            const res = await forgotPassword(email);
            setSuccess(true);
            // For demo purposes, show the reset link (MOCKED)
            if (res.data.debug_reset_link) {
                setDebugInfo(res.data);
            }
        } catch (err) {
            setError(err.response?.data?.detail || 'Failed to process request. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Layout>
            <div className="min-h-[70vh] bg-gray-50 py-12">
                <div className="max-w-md mx-auto px-4">
                    <Link to="/advertiser/login" className="inline-flex items-center text-gray-600 hover:text-navy mb-6">
                        <ArrowLeft className="w-4 h-4 mr-2" />
                        Back to Login
                    </Link>

                    <Card>
                        <CardHeader className="text-center">
                            <div className="w-12 h-12 bg-navy/10 rounded-full flex items-center justify-center mx-auto mb-4">
                                <Mail className="w-6 h-6 text-navy" />
                            </div>
                            <CardTitle className="text-2xl">Forgot Password?</CardTitle>
                            <CardDescription>
                                Enter your email address and we&apos;ll send you a link to reset your password.
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            {success ? (
                                <div className="space-y-4">
                                    <Alert className="bg-green-50 border-green-200">
                                        <CheckCircle className="h-4 w-4 text-green-600" />
                                        <AlertDescription className="text-green-800">
                                            If an account exists with this email, you will receive a password reset link.
                                        </AlertDescription>
                                    </Alert>

                                    {/* MOCKED: Show reset link for demo */}
                                    {debugInfo && (
                                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mt-4">
                                            <p className="text-amber-800 font-semibold text-sm mb-2">
                                                🛠️ Demo Mode - Reset Link:
                                            </p>
                                            <p className="text-xs text-amber-600 mb-3">
                                                In production, this would be sent via email. For demo purposes:
                                            </p>
                                            <Link 
                                                to={debugInfo.debug_reset_link}
                                                className="block bg-navy text-white text-center py-2 px-4 rounded-lg hover:bg-navy/90 transition-colors"
                                            >
                                                Click here to reset password
                                            </Link>
                                        </div>
                                    )}

                                    <div className="text-center pt-4">
                                        <Link to="/advertiser/login">
                                            <Button variant="outline" className="w-full">
                                                Return to Login
                                            </Button>
                                        </Link>
                                    </div>
                                </div>
                            ) : (
                                <form onSubmit={handleSubmit} className="space-y-4">
                                    {error && (
                                        <Alert variant="destructive">
                                            <AlertCircle className="h-4 w-4" />
                                            <AlertDescription>{error}</AlertDescription>
                                        </Alert>
                                    )}

                                    <div className="space-y-2">
                                        <Label htmlFor="email">Email Address</Label>
                                        <Input
                                            id="email"
                                            type="email"
                                            placeholder="your@email.com"
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            required
                                            data-testid="forgot-password-email"
                                        />
                                    </div>

                                    <Button 
                                        type="submit" 
                                        className="w-full bg-navy hover:bg-navy/90"
                                        disabled={loading}
                                        data-testid="forgot-password-submit"
                                    >
                                        {loading ? 'Sending...' : 'Send Reset Link'}
                                    </Button>

                                    <p className="text-center text-sm text-gray-500">
                                        Remember your password?{' '}
                                        <Link to="/advertiser/login" className="text-navy hover:underline">
                                            Sign in
                                        </Link>
                                    </p>
                                </form>
                            )}
                        </CardContent>
                    </Card>
                </div>
            </div>
        </Layout>
    );
};

export default ForgotPasswordPage;
